import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  LayoutDashboard, Users, DollarSign, FolderOpen, Trophy, Settings,
  LogOut, Loader2, Lock, Eye, EyeOff, ChevronRight, ChevronDown,
  Zap, Crown, TrendingUp, Target, Shield, Flame, Star, Award, Gem,
  Menu, X, Download, Image, Video, Camera, Upload, UserPlus, ArrowRight,
  ChevronUp, Sparkles, Copy
} from 'lucide-react';
import { toast } from 'sonner';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  AreaChart, Area, BarChart, Bar
} from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;

const fmt = (n) => Number(n || 0).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtInt = (n) => Number(n || 0).toLocaleString('en-MY');

const ICON_MAP = {
  'zap': Zap, 'dollar-sign': DollarSign, 'crown': Crown, 'trending-up': TrendingUp,
  'award': Award, 'target': Target, 'shield': Shield, 'flame': Flame,
  'gem': Gem, 'star': Star, 'trophy': Trophy,
};

const LEVEL_COLORS = {
  'Starter': '#94A3B8',
  'Builder': '#60A5FA',
  'Accelerator': '#34D399',
  'Influencer': '#A78BFA',
  'Growth Partner': '#FB923C',
  'Elite Creator': '#F472B6',
  'Legend': '#FFD700',
};

// Floating background elements
function FloatingElements() {
  const elements = useMemo(() => Array.from({ length: 25 }, (_, i) => ({
    id: i,
    left: Math.random() * 100,
    top: Math.random() * 100,
    size: 4 + Math.random() * 12,
    duration: 15 + Math.random() * 25,
    delay: Math.random() * 15,
    opacity: 0.04 + Math.random() * 0.08,
    isOrange: Math.random() > 0.5,
    shape: Math.random() > 0.6 ? 'circle' : Math.random() > 0.3 ? 'diamond' : 'hex',
  })), []);

  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden" style={{ zIndex: 0 }}>
      <style>{`
        @keyframes floatOrb { 
          0%, 100% { transform: translate(0, 0) rotate(0deg); }
          25% { transform: translate(30px, -40px) rotate(90deg); }
          50% { transform: translate(-20px, -80px) rotate(180deg); }
          75% { transform: translate(40px, -30px) rotate(270deg); }
        }
      `}</style>
      {elements.map(e => (
        <div key={e.id} style={{
          position: 'absolute', left: `${e.left}%`, top: `${e.top}%`,
          width: e.size, height: e.size,
          borderRadius: e.shape === 'circle' ? '50%' : e.shape === 'diamond' ? '2px' : '4px',
          transform: e.shape === 'diamond' ? 'rotate(45deg)' : 'none',
          backgroundColor: e.isOrange ? '#FF9A00' : '#ffffff',
          opacity: e.opacity,
          animation: `floatOrb ${e.duration}s ${e.delay}s ease-in-out infinite`,
        }} />
      ))}
    </div>
  );
}

// Auth Gate
function AuthGate({ onAuth }) {
  const [step, setStep] = useState(0); // 0=credentials, 1=pin
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [preToken, setPreToken] = useState('');
  const [combo, setCombo] = useState(['', '', '']);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const numbers = Array.from({ length: 100 }, (_, i) => String(i).padStart(2, '0'));

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password.trim()) { setError('Username & password required'); return; }
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API}/api/partner/auth`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.detail || 'Invalid credentials'); setLoading(false); return; }
      setPreToken(data.pre_token);
      setStep(1);
    } catch { setError('Connection error'); }
    setLoading(false);
  };

  const handlePin = async (e) => {
    e.preventDefault();
    if (combo.some(c => c === '')) { setError('Select all 3 numbers'); return; }
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API}/api/partner/verify-pin`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pre_token: preToken, pin_combo: combo }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.detail || 'Invalid PIN'); setLoading(false); return; }
      onAuth(data.token, data.partner);
    } catch { setError('Connection error'); }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: '#195259' }}>
      <FloatingElements />
      <div className="relative z-10 w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4"
            style={{ background: 'rgba(255,154,0,0.15)', border: '1px solid rgba(255,154,0,0.3)' }}>
            <Shield className="w-8 h-8 text-[#FF9A00]" />
          </div>
          <h1 className="text-3xl font-bold text-[#F5E9CF] font-[Lexend]">Partner Portal</h1>
          <p className="text-[#B2C7C9] mt-2 text-sm">Funnely Partnership Program</p>
        </div>

        {step === 0 ? (
          <form onSubmit={handleLogin} data-testid="partner-auth-form"
            className="p-6 rounded-2xl space-y-4"
            style={{ background: 'rgba(255,255,255,0.05)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255,255,255,0.1)' }}>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold" style={{ background: '#FF9A00', color: '#195259' }}>1</div>
              <p className="text-xs uppercase tracking-widest font-semibold text-[#B2C7C9]">Login Credentials</p>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest font-semibold text-[#FF9A00] mb-1 block">Username</label>
              <Input value={username} onChange={e => setUsername(e.target.value)} placeholder="Enter username"
                className="bg-black/20 border-white/10 text-[#F5E9CF] placeholder:text-white/30 focus:border-[#FF9A00]"
                data-testid="partner-login-username" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest font-semibold text-[#FF9A00] mb-1 block">Password</label>
              <div className="relative">
                <Input type={showPw ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="Enter password"
                  className="bg-black/20 border-white/10 text-[#F5E9CF] placeholder:text-white/30 focus:border-[#FF9A00] pr-10"
                  data-testid="partner-login-password" />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70">
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            {error && <p className="text-red-400 text-sm" data-testid="partner-login-error">{error}</p>}
            <Button type="submit" disabled={loading}
              className="w-full rounded-xl font-bold text-[#195259] hover:scale-[1.02] transition-transform"
              style={{ background: '#FF9A00' }} data-testid="partner-login-submit">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Continue <ArrowRight className="ml-2 h-4 w-4" /></>}
            </Button>
          </form>
        ) : (
          <form onSubmit={handlePin} data-testid="partner-pin-form"
            className="p-6 rounded-2xl space-y-4"
            style={{ background: 'rgba(255,255,255,0.05)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255,255,255,0.1)' }}>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold" style={{ background: '#FF9A00', color: '#195259' }}>2</div>
              <p className="text-xs uppercase tracking-widest font-semibold text-[#B2C7C9]">Security Verification</p>
            </div>
            <p className="text-sm text-[#B2C7C9]">Enter your 3-digit PIN combo to continue</p>
            <div className="grid grid-cols-3 gap-3">
              {[0, 1, 2].map(i => (
                <Select key={i} value={combo[i]} onValueChange={v => { const c = [...combo]; c[i] = v; setCombo(c); }}>
                  <SelectTrigger className="bg-black/20 border-white/10 text-[#F5E9CF] focus:border-[#FF9A00] h-12 text-lg font-bold text-center"
                    data-testid={`pin-select-${i}`}>
                    <SelectValue placeholder="--" />
                  </SelectTrigger>
                  <SelectContent className="max-h-48">
                    {numbers.map(n => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                  </SelectContent>
                </Select>
              ))}
            </div>
            {error && <p className="text-red-400 text-sm" data-testid="partner-pin-error">{error}</p>}
            <Button type="submit" disabled={loading}
              className="w-full rounded-xl font-bold text-[#195259] hover:scale-[1.02] transition-transform"
              style={{ background: '#FF9A00' }} data-testid="partner-pin-submit">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Enter Dashboard <Lock className="ml-2 h-4 w-4" /></>}
            </Button>
            <button type="button" onClick={() => { setStep(0); setError(''); setCombo(['', '', '']); }}
              className="w-full text-center text-xs text-[#B2C7C9] hover:text-[#FF9A00] transition-colors mt-2">
              Back to login
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

// Sidebar Navigation
const NAV_ITEMS = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'users', label: 'User Overview', icon: Users },
  { id: 'income', label: 'Income', icon: DollarSign },
  { id: 'library', label: 'Creative Library', icon: FolderOpen },
  { id: 'achievements', label: 'Achievements', icon: Trophy },
  { id: 'settings', label: 'Settings', icon: Settings },
];

function Sidebar({ active, onChange, partner, onLogout, mobileOpen, onMobileClose }) {
  const content = (
    <div className="flex flex-col h-full" style={{ background: '#0F3338' }}>
      <div className="p-5 pb-3 border-b border-white/5">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(255,154,0,0.2)' }}>
            <Sparkles className="w-4 h-4 text-[#FF9A00]" />
          </div>
          <div>
            <p className="font-bold text-[#F5E9CF] text-sm font-[Lexend]">Funnely</p>
            <p className="text-[10px] text-[#FF9A00] uppercase tracking-widest">Partner Hub</p>
          </div>
        </div>
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto" data-testid="partner-sidebar">
        {NAV_ITEMS.map(item => {
          const Icon = item.icon;
          const isActive = active === item.id;
          return (
            <button key={item.id} onClick={() => { onChange(item.id); onMobileClose?.(); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${isActive
                ? 'text-[#195259] shadow-lg shadow-[#FF9A00]/20' : 'text-[#B2C7C9] hover:text-[#F5E9CF] hover:bg-white/5'}`}
              style={isActive ? { background: '#FF9A00' } : {}}
              data-testid={`nav-${item.id}`}>
              <Icon className="h-4 w-4 shrink-0" />
              {item.label}
            </button>
          );
        })}
      </nav>
      <div className="p-3 border-t border-white/5">
        <div className="flex items-center gap-3 px-3 py-2">
          <div className="h-9 w-9 rounded-full flex items-center justify-center text-sm font-bold text-white"
            style={{ background: 'rgba(255,154,0,0.2)', border: '1px solid rgba(255,154,0,0.3)' }}>
            {partner?.display_name?.[0]?.toUpperCase() || 'P'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-[#F5E9CF] truncate">{partner?.display_name || 'Partner'}</p>
            <p className="text-[10px] text-[#B2C7C9] truncate">{partner?.referral_code}</p>
          </div>
          <button onClick={onLogout} className="text-[#B2C7C9] hover:text-red-400 transition-colors" data-testid="partner-logout">
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex lg:w-[260px] flex-col shrink-0 fixed left-0 top-0 h-screen z-30">
        {content}
      </aside>
      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/60" onClick={onMobileClose} />
          <div className="relative w-[280px] h-full">{content}</div>
        </div>
      )}
    </>
  );
}

// ==================== DASHBOARD SECTION ====================
function DashboardSection({ data }) {
  const level = data?.level || {};
  const levels = data?.levels || [];
  const [showLevels, setShowLevels] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(0);
  const [showTotal, setShowTotal] = useState(false);
  const [showBonusDetail, setShowBonusDetail] = useState(false);

  const breakdown = data?.monthly_breakdown || [];
  const isAllMonths = selectedMonth === -1;
  const md = isAllMonths ? {} : (breakdown[selectedMonth] || {});
  const commValue = isAllMonths ? (data?.commission?.total || 0) : (selectedMonth === 0 ? (data?.monthly_commission || 0) : (md.commission || 0));
  const commLabel = isAllMonths ? 'All Months (Total)' : (md.month || data?.current_month || 'This Month');
  const monthUsers = isAllMonths ? (data?.total_users || 0) : (md.signups || 0);
  const monthFree = isAllMonths ? (data?.free_users || 0) : (md.signups_free || 0);
  const monthPro = isAllMonths ? (data?.pro_users || 0) : (md.signups_pro || 0);
  const monthPrem = isAllMonths ? (data?.premium_users || 0) : (md.signups_premium || 0);
  const monthPaid = isAllMonths ? (data?.paid_users || 0) : (md.paid_active || 0);

  return (
    <div className="space-y-6" data-testid="dashboard-section">
      {/* Month Selector — Top Right */}
      <div className="flex items-center justify-end -mb-2">
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
          <svg className="w-3.5 h-3.5 text-[#FF9A00]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <select value={selectedMonth} onChange={e => setSelectedMonth(Number(e.target.value))}
            className="text-xs bg-transparent text-[#F5E9CF] outline-none cursor-pointer appearance-none pr-4"
            style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%23F5E9CF'/%3E%3C/svg%3E\")", backgroundRepeat: 'no-repeat', backgroundPosition: 'right center' }}
            data-testid="month-selector-dashboard">
            <option value={-1} className="bg-[#0F3338]">All Months</option>
            {breakdown.map((m, i) => <option key={i} value={i} className="bg-[#0F3338]">{m.month}</option>)}
          </select>
        </div>
      </div>

      {/* Commission rate info */}

      {/* Stats Cards — Monthly data */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border-0 overflow-hidden group hover:-translate-y-1 transition-all duration-300"
          style={{ background: 'rgba(255,255,255,0.05)', backdropFilter: 'blur(16px)', border: '1px solid rgba(255,255,255,0.08)' }}>
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-widest font-semibold text-[#94A3B8]">Free Users</p>
                <p className="text-3xl font-bold text-[#F5E9CF] mt-1 font-[Lexend]" data-testid="stat-free-users">{fmtInt(monthFree)}</p>
                <p className="text-[10px] text-[#B2C7C9] mt-1">{isAllMonths ? 'Overall total' : `In ${md.month || ''}`}</p>
              </div>
              <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: '#94A3B815' }}>
                <Users className="w-6 h-6 text-[#94A3B8]" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 overflow-hidden group hover:-translate-y-1 transition-all duration-300"
          style={{ background: 'rgba(255,255,255,0.05)', backdropFilter: 'blur(16px)', border: '1px solid rgba(255,255,255,0.08)' }}>
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-widest font-semibold text-[#FF9A00]">Paid Users</p>
                <p className="text-3xl font-bold text-[#F5E9CF] mt-1 font-[Lexend]" data-testid="stat-paid-users">{fmtInt(monthPaid)}</p>
                <p className="text-[10px] text-[#B2C7C9] mt-1">{isAllMonths ? 'Overall total' : `Active in ${md.month || ''}`}</p>
              </div>
              <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: '#FF9A0015' }}>
                <UserPlus className="w-6 h-6 text-[#FF9A00]" />
              </div>
            </div>
          </CardContent>
        </Card>
        {/* Commission card */}
        <Card className="border-0 overflow-hidden group hover:-translate-y-1 transition-all duration-300"
          style={{ background: 'rgba(255,255,255,0.05)', backdropFilter: 'blur(16px)', border: '1px solid rgba(255,255,255,0.08)' }}>
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-widest font-semibold text-[#4ADE80]">Commission</p>
                <p className="text-3xl font-bold text-[#F5E9CF] mt-1 font-[Lexend]" data-testid="stat-commission">RM{fmt(commValue)}</p>
                <p className="text-[10px] text-[#B2C7C9] mt-0.5">{commLabel}</p>
              </div>
              <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: '#4ADE8015' }}>
                <DollarSign className="w-6 h-6 text-[#4ADE80]" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Total Overall Commission toggle */}
      <div className="flex justify-center">
        <button onClick={() => setShowTotal(!showTotal)}
          className="text-[10px] text-[#B2C7C9] hover:text-[#FF9A00] transition-colors flex items-center gap-1"
          data-testid="toggle-total-commission">
          {showTotal ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          {showTotal ? 'Hide' : 'View'} Total Overall Commission
        </button>
      </div>
      {showTotal && (
        <Card className="border-0" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}>
          <CardContent className="p-4 flex items-center justify-between">
            <p className="text-xs text-[#B2C7C9]">Total Commission (6 Months)</p>
            <p className="text-xl font-bold text-[#F5E9CF] font-[Lexend]">RM{fmt(data?.commission?.total || 0)}</p>
          </CardContent>
        </Card>
      )}

      {/* Promo Code */}
      {data?.partner?.promo_code && (
        <Card className="border-0 overflow-hidden group hover:-translate-y-1 transition-all duration-300"
          style={{ background: 'rgba(255,154,0,0.1)', border: '1px solid rgba(255,154,0,0.25)' }}>
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-widest font-semibold text-[#FF9A00] mb-1">Your Promo Code</p>
                <p className="text-2xl font-bold text-[#F5E9CF] font-[Lexend] tracking-wider" data-testid="dashboard-promo-code">{data.partner.promo_code}</p>
                <p className="text-[10px] text-[#B2C7C9] mt-1">Share this code with your audience</p>
              </div>
              <button onClick={() => { navigator.clipboard.writeText(data.partner.promo_code); toast.success('Promo code copied!'); }}
                className="w-10 h-10 rounded-xl flex items-center justify-center hover:scale-110 transition-transform"
                style={{ background: 'rgba(255,154,0,0.2)' }} data-testid="copy-promo-dashboard">
                <Copy className="w-5 h-5 text-[#FF9A00]" />
              </button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Milestone Progression */}
      <Card className="border-0 overflow-hidden" style={{ background: 'rgba(255,255,255,0.05)', backdropFilter: 'blur(16px)', border: '1px solid rgba(255,255,255,0.08)' }}>
        <CardContent className="p-6">
          {/* Current level summary — always visible */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              {(() => {
                const LEVEL_ICONS = [Zap, TrendingUp, Flame, Crown, Target, Star, Trophy];
                const Icon = LEVEL_ICONS[level.current_index || 0] || Zap;
                const color = LEVEL_COLORS[level.current] || '#FF9A00';
                return (
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center"
                    style={{ background: `linear-gradient(135deg, ${color}, ${color}CC)`, boxShadow: `0 0 16px ${color}40`, border: `2px solid ${color}` }}>
                    <Icon className="w-6 h-6" style={{ color: '#195259' }} />
                  </div>
                );
              })()}
              <div>
                <p className="text-lg font-bold font-[Lexend]" style={{ color: LEVEL_COLORS[level.current] || '#FF9A00' }}>{level.current || 'Starter'}</p>
                <p className="text-xs text-[#B2C7C9]">{level.xp || 0} XP{level.next ? ` • ${level.needed} more to ${level.next}` : ' • Max level!'}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-xs text-[#B2C7C9]">{level.progress || 0}%</p>
            </div>
          </div>

          {/* XP Bar */}
          <div className="mb-3">
            <div className="h-4 rounded-full overflow-hidden" style={{ background: 'rgba(0,0,0,0.3)' }}>
              <div className="h-full rounded-full transition-all duration-1000 ease-out"
                style={{
                  width: `${Math.min(level.progress || 0, 100)}%`,
                  background: 'linear-gradient(90deg, #FF9A00, #FFB033)',
                  boxShadow: '0 0 15px rgba(255,154,0,0.5)',
                }} />
            </div>
          </div>

          {/* Toggle to expand all levels */}
          <button onClick={() => setShowLevels(!showLevels)}
            className="text-[10px] text-[#FF9A00] hover:text-[#FFB033] font-semibold flex items-center gap-1 transition-colors"
            data-testid="toggle-levels-detail">
            {showLevels ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            {showLevels ? 'Hide' : 'View'} All Levels
          </button>

          {showLevels && (
            <div className="mt-4 space-y-2">
              {levels.map((l, i) => {
                const isActive = level.current === l.name;
                const isPassed = (level.current_index || 0) > i;
                const isLocked = !isPassed && !isActive;
                const color = LEVEL_COLORS[l.name] || '#94A3B8';
                const needed = l.threshold - (level.paid_users || 0);
                const tierSize = 10 + i * 2;
                const LEVEL_ICONS = [Zap, TrendingUp, Flame, Crown, Target, Star, Trophy];
                const Icon = LEVEL_ICONS[i] || Trophy;
                return (
                  <div key={l.name}
                    className={`flex items-center gap-4 p-3 rounded-xl transition-all duration-300 ${isActive ? 'scale-[1.02]' : ''}`}
                    style={{
                      background: isActive ? `linear-gradient(135deg, ${color}20, ${color}08)` : isPassed ? `${color}08` : 'rgba(255,255,255,0.02)',
                      border: `1px solid ${isActive ? `${color}40` : isPassed ? `${color}15` : 'rgba(255,255,255,0.05)'}`,
                      boxShadow: isActive ? `0 0 20px ${color}15` : 'none',
                      opacity: isLocked ? 0.55 : 1,
                    }}>
                    <div className="shrink-0 relative">
                      <div className="flex items-center justify-center rounded-xl"
                        style={{
                          width: tierSize + 28, height: tierSize + 28,
                          background: isPassed || isActive ? `linear-gradient(135deg, ${color}, ${color}CC)` : 'rgba(255,255,255,0.06)',
                          boxShadow: isActive ? `0 0 16px ${color}50` : isPassed ? `0 2px 8px ${color}30` : 'none',
                          border: `2px solid ${isPassed || isActive ? color : 'rgba(255,255,255,0.1)'}`,
                        }}>
                        <Icon style={{ width: tierSize + 4, height: tierSize + 4, color: isPassed || isActive ? '#195259' : '#6B7280' }} />
                      </div>
                      {isActive && <div className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full animate-pulse" style={{ background: color, boxShadow: `0 0 8px ${color}` }} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-bold" style={{ color: isPassed || isActive ? color : '#6B7280' }}>{l.name}</p>
                        {isPassed && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full" style={{ background: '#4ADE8020', color: '#4ADE80' }}>CLEARED</span>}
                        {isActive && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full animate-pulse" style={{ background: `${color}25`, color }}>CURRENT</span>}
                        {isLocked && <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(255,255,255,0.08)', color: '#9CA3AF', border: '1px solid rgba(255,255,255,0.1)' }}>LOCKED</span>}
                      </div>
                      <p className="text-[10px]" style={{ color: isPassed || isActive ? '#B2C7C9' : '#6B7280' }}>
                        {l.threshold === 0 ? 'Starting level' : `${l.threshold} paid users`}
                        {isActive && needed > 0 ? ` • ${needed} more to next` : ''}
                        {isLocked && needed > 0 ? ` • ${needed} more needed` : ''}
                      </p>
                    </div>
                    <div className="shrink-0">
                      <p className="text-xs font-bold" style={{ color: isPassed || isActive ? color : '#6B7280' }}>{fmtInt(l.xp)} XP</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Next Action */}
      {data?.next_action && (
        <Card className="border-0 overflow-hidden group hover:-translate-y-1 transition-all duration-300"
          style={{ background: 'linear-gradient(135deg, rgba(255,154,0,0.15), rgba(255,154,0,0.05))', border: '1px solid rgba(255,154,0,0.2)' }}>
          <CardContent className="p-5 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'rgba(255,154,0,0.2)' }}>
              <Zap className="w-6 h-6 text-[#FF9A00]" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest font-semibold text-[#FF9A00] mb-1">Next Action</p>
              <p className="text-sm text-[#F5E9CF] font-medium" data-testid="next-action-text">{data.next_action}</p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ==================== USER OVERVIEW SECTION ====================
function UserOverviewSection({ token }) {
  const [data, setData] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('all_months');
  const [filterPlan, setFilterPlan] = useState('all');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [dRes, uRes] = await Promise.all([
          fetch(`${API}/api/partner/dashboard`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${API}/api/partner/users?limit=200`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        if (dRes.ok) setData(await dRes.json());
        if (uRes.ok) { const u = await uRes.json(); setUsers(u.users || []); }
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    fetchData();
  }, [token]);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#FF9A00]" /></div>;

  const total = data?.total_users || 0;
  const free = data?.free_users || 0;
  const pro = data?.pro_users || 0;
  const premium = data?.premium_users || 0;
  const paid = pro + premium;
  const conversionRate = total > 0 ? ((paid / total) * 100).toFixed(1) : '0.0';

  const planOrder = { premium: 0, pro: 1, free: 2 };
  const filtered = users.filter(u => {
    if (filterPlan !== 'all' && (u.plan || 'free') !== filterPlan) return false;
    if (sortBy !== 'all_months') {
      const d = new Date(u.created_at || 0);
      const userMonth = d.toLocaleString('en-US', { month: 'short' });
      if (userMonth !== sortBy) return false;
    }
    return true;
  });
  const sorted = [...filtered].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
  const planColors = { free: '#94A3B8', pro: '#A78BFA', premium: '#FFD700' };
  const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

  return (
    <div className="space-y-6" data-testid="user-overview-section">
      {/* User Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Total Users', value: total, color: '#60A5FA' },
          { label: 'Free Users', value: free, color: '#94A3B8' },
          { label: 'Pro Users', value: pro, color: '#A78BFA' },
          { label: 'Premium Users', value: premium, color: '#FFD700' },
        ].map((s, i) => (
          <Card key={i} className="border-0" style={{ background: 'rgba(255,255,255,0.05)', backdropFilter: 'blur(16px)', border: '1px solid rgba(255,255,255,0.08)' }}>
            <CardContent className="p-4 text-center">
              <p className="text-xs uppercase tracking-widest font-semibold mb-1" style={{ color: s.color }}>{s.label}</p>
              <p className="text-2xl font-bold text-[#F5E9CF] font-[Lexend]" data-testid={`user-stat-${s.label.toLowerCase().replace(' ', '-')}`}>{s.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Conversion Rate */}
      <Card className="border-0" style={{ background: 'rgba(255,255,255,0.05)', backdropFilter: 'blur(16px)', border: '1px solid rgba(255,255,255,0.08)' }}>
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-xs uppercase tracking-widest font-semibold text-[#FF9A00]">Conversion Rate</p>
              <p className="text-sm text-[#B2C7C9]">Free to Paid conversion</p>
            </div>
            <p className="text-3xl font-bold text-[#4ADE80] font-[Lexend]" data-testid="conversion-rate">{conversionRate}%</p>
          </div>
          <div className="flex gap-2 h-3 rounded-full overflow-hidden" style={{ background: 'rgba(0,0,0,0.3)' }}>
            {free > 0 && <div style={{ width: `${(free / total) * 100}%`, background: '#94A3B8' }} className="rounded-full" />}
            {pro > 0 && <div style={{ width: `${(pro / total) * 100}%`, background: '#A78BFA' }} className="rounded-full" />}
            {premium > 0 && <div style={{ width: `${(premium / total) * 100}%`, background: '#FFD700' }} className="rounded-full" />}
          </div>
          <div className="flex gap-4 mt-2 text-[10px]">
            <span className="flex items-center gap-1" style={{ color: '#94A3B8' }}><div className="w-2 h-2 rounded-full" style={{ background: '#94A3B8' }} /> Free</span>
            <span className="flex items-center gap-1" style={{ color: '#A78BFA' }}><div className="w-2 h-2 rounded-full" style={{ background: '#A78BFA' }} /> Pro</span>
            <span className="flex items-center gap-1" style={{ color: '#FFD700' }}><div className="w-2 h-2 rounded-full" style={{ background: '#FFD700' }} /> Premium</span>
          </div>
        </CardContent>
      </Card>

      {/* Real-time User Log */}
      <Card className="border-0" style={{ background: 'rgba(255,255,255,0.05)', backdropFilter: 'blur(16px)', border: '1px solid rgba(255,255,255,0.08)' }}>
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <h3 className="text-lg font-bold text-[#F5E9CF] font-[Lexend] flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" /> User Activity Log
            </h3>
            <div className="flex items-center gap-2">
              <select value={filterPlan} onChange={e => setFilterPlan(e.target.value)}
                className="text-[10px] bg-black/20 border border-white/10 rounded-lg px-2 py-1 text-[#F5E9CF] outline-none cursor-pointer"
                data-testid="filter-plan">
                <option value="all" className="bg-[#0F3338]">All Plans</option>
                <option value="premium" className="bg-[#0F3338]">Premium</option>
                <option value="pro" className="bg-[#0F3338]">Pro</option>
                <option value="free" className="bg-[#0F3338]">Free</option>
              </select>
              <select value={sortBy} onChange={e => setSortBy(e.target.value)}
                className="text-[10px] bg-black/20 border border-white/10 rounded-lg px-2 py-1 text-[#F5E9CF] outline-none cursor-pointer"
                data-testid="sort-by">
                <option value="all_months" className="bg-[#0F3338]">All Months</option>
                {MONTHS.map(m => <option key={m} value={m} className="bg-[#0F3338]">{m}</option>)}
              </select>
            </div>
          </div>
          <div className="space-y-2 max-h-80 overflow-y-auto" data-testid="user-activity-log">
            {sorted.length === 0 && <p className="text-[#B2C7C9] text-sm text-center py-8">{sortBy !== 'all_months' ? `No users signed up in ${sortBy}` : 'No referred users yet. Share your referral code!'}</p>}
            {sorted.map((u, i) => {
              const plan = u.plan || 'free';
              return (
                <div key={u.id || i} className="flex items-center justify-between p-3 rounded-xl hover:bg-white/5 transition-colors"
                  style={{ border: '1px solid rgba(255,255,255,0.03)' }}>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold"
                      style={{ background: `${planColors[plan]}20`, color: planColors[plan] }}>
                      {u.name?.[0]?.toUpperCase() || '?'}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-[#F5E9CF]">{u.name || u.email}</p>
                      <p className="text-[10px] text-[#B2C7C9]">{u.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge className="text-[10px] rounded-full font-bold border"
                      style={{ background: `${planColors[plan]}20`, color: planColors[plan], borderColor: `${planColors[plan]}40` }}>
                      {plan.toUpperCase()}
                    </Badge>
                    <span className="text-[10px] text-[#B2C7C9]">{u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ==================== INCOME CALCULATOR ====================
function IncomeCalc() {
  const [proCount, setProCount] = useState('');
  const [premCount, setPremCount] = useState('');
  const [result, setResult] = useState(null);

  const calculate = () => {
    const pro = parseInt(proCount) || 0;
    const prem = parseInt(premCount) || 0;
    // 40% flat commission every month
    const proComm = pro * 69 * 0.40;
    const premComm = prem * 99 * 0.40;
    const monthlyTotal = proComm + premComm;
    const totalPaid = pro + prem;
    setResult({ pro, prem, proComm, premComm, monthlyTotal, totalPaid });
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] uppercase tracking-widest font-semibold text-[#A78BFA] mb-1 block">Pro Users</label>
          <Input type="number" min="0" value={proCount} onChange={e => setProCount(e.target.value)} placeholder="0"
            className="bg-black/20 border-white/10 text-[#F5E9CF] focus:border-[#FF9A00]" data-testid="calc-pro-input" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest font-semibold text-[#FFD700] mb-1 block">Premium Users</label>
          <Input type="number" min="0" value={premCount} onChange={e => setPremCount(e.target.value)} placeholder="0"
            className="bg-black/20 border-white/10 text-[#F5E9CF] focus:border-[#FF9A00]" data-testid="calc-prem-input" />
        </div>
      </div>
      <Button onClick={calculate} className="w-full rounded-xl font-bold text-[#195259]" style={{ background: '#FF9A00' }} data-testid="calc-submit">
        Calculate Commission
      </Button>
      {result && (
        <div className="mt-3 space-y-3">
          {/* Month 1 */}
          <div className="p-3 rounded-xl" style={{ background: 'rgba(255,154,0,0.06)', border: '1px solid rgba(255,154,0,0.12)' }}>
            <p className="text-[10px] font-bold uppercase tracking-widest text-[#FF9A00] mb-2">Monthly Commission (40% Flat)</p>
            <div className="space-y-1 text-xs text-[#B2C7C9]">
              {result.pro > 0 && <div className="flex justify-between"><span>{result.pro} Pro x RM27.60/mo</span><span className="text-[#A78BFA] font-semibold">RM{fmt(result.proComm)}</span></div>}
              {result.prem > 0 && <div className="flex justify-between"><span>{result.prem} Premium x RM39.60/mo</span><span className="text-[#FFD700] font-semibold">RM{fmt(result.premComm)}</span></div>}
            </div>
            <div className="flex justify-between pt-1.5 mt-1.5 border-t border-white/10 text-xs">
              <span className="text-[#B2C7C9]">Monthly Commission (40%)</span>
              <span className="font-bold text-[#F5E9CF]">RM{fmt(result.monthlyTotal)}</span>
            </div>
          </div>
          {/* Grand Total */}
          <div className="p-3 rounded-xl flex justify-between items-center" style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)' }}>
            <span className="text-sm font-semibold text-[#B2C7C9]">Monthly Recurring</span>
            <span className="text-2xl font-bold text-[#F5E9CF] font-[Lexend]">RM{fmt(result.monthlyTotal)}/mo</span>
          </div>
        </div>
      )}
    </div>
  );
}

// ==================== INCOME SECTION ====================
function IncomeSection({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(0);
  const [showGeneral, setShowGeneral] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch(`${API}/api/partner/income`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) setData(await res.json());
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    fetchData();
  }, [token]);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#FF9A00]" /></div>;

  const comm = data?.commission || {};
  const breakdown = data?.monthly_breakdown || [];
  const current = breakdown[selectedMonth] || {};
  const chartData = [...breakdown].reverse().map(m => ({ name: m.month, value: m.commission }));

  return (
    <div className="space-y-6" data-testid="income-section">
      {/* Monthly Commission — Main Card */}
      <Card className="border-0 overflow-hidden"
        style={{ background: 'linear-gradient(135deg, rgba(74,222,128,0.12), rgba(255,154,0,0.08))', border: '1px solid rgba(74,222,128,0.2)' }}>
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs uppercase tracking-widest font-semibold text-[#4ADE80]">Monthly Commission</p>
            <select value={selectedMonth} onChange={e => setSelectedMonth(Number(e.target.value))}
              className="text-xs bg-black/20 border border-white/10 rounded-lg px-3 py-1.5 text-[#F5E9CF] outline-none focus:border-[#FF9A00] cursor-pointer"
              data-testid="month-selector-income">
              {breakdown.map((m, i) => <option key={i} value={i} className="bg-[#0F3338]">{m.month}</option>)}
            </select>
          </div>
          <p className="text-4xl font-bold text-[#F5E9CF] font-[Lexend] text-center" data-testid="monthly-income">
            RM{fmt(current.commission || 0)}
          </p>
          <div className="flex justify-center gap-6 mt-3 text-xs text-[#B2C7C9]">
            <span>{current.pro_users || 0} Pro users</span>
            <span>{current.premium_users || 0} Premium users</span>
            <span className="text-[#FF9A00] font-semibold">40% commission</span>
          </div>
        </CardContent>
      </Card>

      {/* Detailed Monthly Calculation */}
      <Card className="border-0" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
        <CardContent className="p-5 space-y-4">
          <h4 className="text-sm font-bold text-[#F5E9CF]">How RM{fmt(current.commission || 0)} is calculated for {current.month}</h4>
          <p className="text-[10px] text-[#B2C7C9]">Commission rate: 40% flat on every active subscriber</p>

          {/* Pro users */}
          {(current.pro_users || 0) > 0 && (
            <div className="p-3 rounded-xl" style={{ background: 'rgba(167,139,250,0.06)', border: '1px solid rgba(167,139,250,0.15)' }}>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-2.5 h-2.5 rounded-full" style={{ background: '#A78BFA' }} />
                <p className="text-xs font-semibold text-[#A78BFA]">Pro Plan Subscribers</p>
              </div>
              <div className="flex justify-between text-xs text-[#B2C7C9]">
                <span>{current.pro_users} users x RM69 x 40%</span>
                <span className="text-[#A78BFA] font-bold">RM{fmt((current.pro_users || 0) * 69 * 0.4)}</span>
              </div>
              <div className="flex justify-between text-[10px] text-[#B2C7C9]/60 mt-1">
                <span>= {current.pro_users} x RM27.60/user</span>
                <span>RM27.60 per Pro user/month</span>
              </div>
            </div>
          )}

          {/* Premium users */}
          {(current.premium_users || 0) > 0 && (
            <div className="p-3 rounded-xl" style={{ background: 'rgba(255,215,0,0.06)', border: '1px solid rgba(255,215,0,0.15)' }}>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-2.5 h-2.5 rounded-full" style={{ background: '#FFD700' }} />
                <p className="text-xs font-semibold text-[#FFD700]">Premium Plan Subscribers</p>
              </div>
              <div className="flex justify-between text-xs text-[#B2C7C9]">
                <span>{current.premium_users} users x RM99 x 40%</span>
                <span className="text-[#FFD700] font-bold">RM{fmt((current.premium_users || 0) * 99 * 0.4)}</span>
              </div>
              <div className="flex justify-between text-[10px] text-[#B2C7C9]/60 mt-1">
                <span>= {current.premium_users} x RM39.60/user</span>
                <span>RM39.60 per Premium user/month</span>
              </div>
            </div>
          )}

          {/* Total */}
          <div className="p-3 rounded-xl flex justify-between items-center" style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)' }}>
            <div>
              <span className="text-sm font-semibold text-[#B2C7C9]">Total {current.month}</span>
              <p className="text-[10px] text-[#B2C7C9]/60">{(current.pro_users||0) + (current.premium_users||0)} active paid users</p>
            </div>
            <span className="text-xl font-bold text-[#F5E9CF] font-[Lexend]">RM{fmt(current.commission || 0)}</span>
          </div>
        </CardContent>
      </Card>

      {/* Total Commission — Secondary */}
      <Card className="border-0 overflow-hidden"
        style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
        <CardContent className="p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-widest font-semibold text-[#FF9A00]">Total Commission (6 Months)</p>
              <p className="text-sm text-[#B2C7C9]">{(comm.paid_pro || 0) + (comm.paid_premium || 0)} paid users</p>
            </div>
            <p className="text-2xl font-bold text-[#F5E9CF] font-[Lexend]" data-testid="total-income">RM{fmt(comm.total || 0)}</p>
          </div>
        </CardContent>
      </Card>

      {/* Monthly Breakdown Chart */}
      <Card className="border-0" style={{ background: 'rgba(255,255,255,0.05)', backdropFilter: 'blur(16px)', border: '1px solid rgba(255,255,255,0.08)' }}>
        <CardContent className="p-5">
          <h3 className="text-lg font-bold text-[#F5E9CF] font-[Lexend] mb-4">Monthly Performance</h3>
          <div className="h-48">
            <ResponsiveContainer>
              <BarChart data={chartData}>
                <XAxis dataKey="name" tick={{ fill: '#B2C7C9', fontSize: 10 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: '#B2C7C9', fontSize: 10 }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ background: '#0F3338', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: '#F5E9CF', fontSize: 12 }}
                  formatter={(v) => [`RM${v.toFixed(2)}`, 'Commission']} />
                <Bar dataKey="value" fill="#FF9A00" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* General Commission Structure — Collapsed */}
      <div>
        <button onClick={() => setShowGeneral(!showGeneral)}
          className="flex items-center gap-2 text-xs font-semibold text-[#B2C7C9] hover:text-[#F5E9CF] transition-colors w-full justify-center py-2"
          data-testid="toggle-general">
          {showGeneral ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          {showGeneral ? 'Hide' : 'View'} Commission Structure
        </button>
        {showGeneral && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
            <Card className="border-0 overflow-hidden" style={{ background: 'rgba(255,215,0,0.08)', border: '1px solid rgba(255,215,0,0.2)' }}>
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Crown className="w-5 h-5 text-[#FFD700]" />
                  <h4 className="text-sm font-bold text-[#FFD700] uppercase tracking-wider">Premium Plan</h4>
                </div>
                <div className="space-y-1 text-xs text-[#B2C7C9]">
                  <p>RM99/user subscription</p>
                  <p>40% commission = RM39.60/user/month</p>
                  <div className="pt-2 border-t border-white/10 mt-2">
                    <p className="text-[#FFD700] font-semibold">RM39.60 recurring every month</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-0 overflow-hidden" style={{ background: 'rgba(167,139,250,0.08)', border: '1px solid rgba(167,139,250,0.2)' }}>
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Star className="w-5 h-5 text-[#A78BFA]" />
                  <h4 className="text-sm font-bold text-[#A78BFA] uppercase tracking-wider">Pro Plan</h4>
                </div>
                <div className="space-y-1 text-xs text-[#B2C7C9]">
                  <p>RM69/user subscription</p>
                  <p>40% commission = RM27.60/user/month</p>
                  <div className="pt-2 border-t border-white/10 mt-2">
                    <p className="text-[#A78BFA] font-semibold">RM27.60 recurring every month</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>


      {/* Income Calculator */}
      <Card className="border-0" style={{ background: 'rgba(255,154,0,0.06)', border: '1px solid rgba(255,154,0,0.15)' }}>
        <CardContent className="p-5">
          <h3 className="text-lg font-bold text-[#F5E9CF] font-[Lexend] mb-1">Income Calculator</h3>
          <p className="text-xs text-[#B2C7C9] mb-4">Simulate your potential earnings</p>
          <IncomeCalc />
        </CardContent>
      </Card>
    </div>
  );
}

// ==================== CREATIVE LIBRARY SECTION ====================
function CreativeLibrarySection({ token }) {
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch(`${API}/api/partner/creative-library`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) { const d = await res.json(); setMaterials(d.materials || []); }
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    fetchData();
  }, [token]);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#FF9A00]" /></div>;

  const posters = materials.filter(m => m.type === 'poster');
  const videos = materials.filter(m => m.type === 'video');

  return (
    <div className="space-y-6" data-testid="creative-library-section">
      <div>
        <h3 className="text-lg font-bold text-[#F5E9CF] font-[Lexend] mb-1">Creative Library</h3>
        <p className="text-sm text-[#B2C7C9]">Download marketing materials to promote Funnely</p>
      </div>

      {/* Posters */}
      <div>
        <h4 className="text-sm font-bold text-[#FF9A00] uppercase tracking-widest mb-3 flex items-center gap-2">
          <Image className="w-4 h-4" /> Posters
        </h4>
        {posters.length === 0 && <p className="text-sm text-[#B2C7C9] py-4">No posters available yet.</p>}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {posters.map(m => (
            <Card key={m.id} className="border-0 group hover:-translate-y-1 transition-all duration-300 cursor-pointer"
              style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <CardContent className="p-4">
                {m.thumbnail_url && <img src={m.thumbnail_url} alt={m.title} className="w-full h-32 object-cover rounded-lg mb-3" />}
                <p className="text-sm font-medium text-[#F5E9CF] mb-2">{m.title}</p>
                <a href={m.file_url} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1 text-xs font-semibold text-[#FF9A00] hover:text-[#FFB033]">
                  <Download className="w-3 h-3" /> Download
                </a>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Videos */}
      <div>
        <h4 className="text-sm font-bold text-[#FF9A00] uppercase tracking-widest mb-3 flex items-center gap-2">
          <Video className="w-4 h-4" /> Videos
        </h4>
        {videos.length === 0 && <p className="text-sm text-[#B2C7C9] py-4">No videos available yet.</p>}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {videos.map(m => (
            <Card key={m.id} className="border-0 group hover:-translate-y-1 transition-all duration-300 cursor-pointer"
              style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <CardContent className="p-4">
                {m.thumbnail_url && <img src={m.thumbnail_url} alt={m.title} className="w-full h-32 object-cover rounded-lg mb-3" />}
                <p className="text-sm font-medium text-[#F5E9CF] mb-2">{m.title}</p>
                <a href={m.file_url} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1 text-xs font-semibold text-[#FF9A00] hover:text-[#FFB033]">
                  <Download className="w-3 h-3" /> Download
                </a>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}

// ==================== ACHIEVEMENTS SECTION ====================
function AchievementsSection({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch(`${API}/api/partner/achievements`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) setData(await res.json());
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    fetchData();
  }, [token]);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#FF9A00]" /></div>;

  const achievements = data?.achievements || [];

  return (
    <div className="space-y-6" data-testid="achievements-section">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-[#F5E9CF] font-[Lexend]">Achievements</h3>
          <p className="text-sm text-[#B2C7C9]">Unlock badges and earn bragging rights</p>
        </div>
        <Badge className="text-sm px-3 py-1 rounded-full font-bold" style={{ background: 'rgba(255,154,0,0.2)', color: '#FF9A00', border: '1px solid rgba(255,154,0,0.3)' }}>
          {data?.unlocked_count || 0} / {data?.total_count || 0}
        </Badge>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {achievements.map(ach => {
          const Icon = ICON_MAP[ach.icon] || Trophy;
          return (
            <Card key={ach.id}
              className={`border-0 overflow-hidden transition-all duration-300 ${ach.unlocked ? 'hover:-translate-y-1' : 'opacity-50'}`}
              style={{
                background: ach.unlocked ? 'rgba(255,154,0,0.08)' : 'rgba(255,255,255,0.03)',
                border: `1px solid ${ach.unlocked ? 'rgba(255,154,0,0.2)' : 'rgba(255,255,255,0.05)'}`,
              }} data-testid={`achievement-${ach.id}`}>
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
                  style={{
                    background: ach.unlocked ? 'rgba(255,154,0,0.2)' : 'rgba(255,255,255,0.05)',
                    boxShadow: ach.unlocked ? '0 0 15px rgba(255,154,0,0.3)' : 'none',
                  }}>
                  <Icon className="w-6 h-6" style={{ color: ach.unlocked ? '#FF9A00' : '#4A5568' }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold" style={{ color: ach.unlocked ? '#FF9A00' : '#4A5568' }}>{ach.title}</p>
                  <p className="text-xs" style={{ color: ach.unlocked ? '#B2C7C9' : '#4A5568' }}>{ach.desc}</p>
                </div>
                {ach.unlocked && (
                  <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: 'rgba(74,222,128,0.2)' }}>
                    <Sparkles className="w-4 h-4 text-[#4ADE80]" />
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

// ==================== SETTINGS SECTION ====================
function SettingsSection({ token, partner, onPartnerUpdate }) {
  const [displayName, setDisplayName] = useState(partner?.display_name || '');
  const [avatarUrl, setAvatarUrl] = useState(partner?.avatar_url || '');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [changingPw, setChangingPw] = useState(false);
  const [showPwForm, setShowPwForm] = useState(false);
  const fileRef = useRef(null);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await fetch(`${API}/api/partner/upload-avatar`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: formData,
      });
      if (res.ok) {
        const data = await res.json();
        setAvatarUrl(data.avatar_url);
        toast.success('Avatar uploaded!');
      } else { toast.error('Upload failed'); }
    } catch { toast.error('Upload error'); }
    setUploading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/partner/profile`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ display_name: displayName, avatar_url: avatarUrl }),
      });
      if (res.ok) {
        const updated = await res.json();
        onPartnerUpdate?.(updated);
        toast.success('Profile updated!');
      } else { toast.error('Update failed'); }
    } catch { toast.error('Error'); }
    setSaving(false);
  };

  const handleChangePassword = async () => {
    if (!currentPw || !newPw) { toast.error('Fill in both fields'); return; }
    if (newPw.length < 6) { toast.error('New password must be at least 6 characters'); return; }
    setChangingPw(true);
    try {
      const res = await fetch(`${API}/api/partner/password`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ current_password: currentPw, new_password: newPw }),
      });
      if (res.ok) {
        toast.success('Password updated!');
        setCurrentPw(''); setNewPw(''); setShowPwForm(false);
      } else { const e = await res.json(); toast.error(e.detail || 'Failed'); }
    } catch { toast.error('Error'); }
    setChangingPw(false);
  };

  const fullAvatarUrl = avatarUrl ? (avatarUrl.startsWith('http') ? avatarUrl : `${API}${avatarUrl}`) : '';

  return (
    <div className="space-y-6 max-w-lg" data-testid="settings-section">
      <div>
        <h3 className="text-lg font-bold text-[#F5E9CF] font-[Lexend] mb-1">Settings</h3>
        <p className="text-sm text-[#B2C7C9]">Manage your partner profile</p>
      </div>

      {/* Profile Card */}
      <Card className="border-0" style={{ background: 'rgba(255,255,255,0.05)', backdropFilter: 'blur(16px)', border: '1px solid rgba(255,255,255,0.08)' }}>
        <CardContent className="p-6 space-y-6">
          {/* Avatar */}
          <div className="flex flex-col items-center">
            <div className="relative group cursor-pointer" onClick={() => fileRef.current?.click()}>
              <div className="w-24 h-24 rounded-full overflow-hidden flex items-center justify-center text-3xl font-bold"
                style={{ background: 'rgba(255,154,0,0.15)', border: '3px solid rgba(255,154,0,0.3)', color: '#FF9A00' }}>
                {fullAvatarUrl ? <img src={fullAvatarUrl} alt="Avatar" className="w-full h-full object-cover" /> : (partner?.display_name?.[0]?.toUpperCase() || 'P')}
              </div>
              <div className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                {uploading ? <Loader2 className="w-6 h-6 animate-spin text-white" /> : <Camera className="w-6 h-6 text-white" />}
              </div>
            </div>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleUpload} data-testid="avatar-upload-input" />
            <p className="text-xs text-[#B2C7C9] mt-2">Click to change avatar</p>
          </div>

          {/* Display Name */}
          <div>
            <label className="text-xs uppercase tracking-widest font-semibold text-[#FF9A00] mb-1 block">Display Name</label>
            <Input value={displayName} onChange={e => setDisplayName(e.target.value)}
              className="bg-black/20 border-white/10 text-[#F5E9CF] focus:border-[#FF9A00]"
              data-testid="settings-display-name" />
          </div>

          {/* Promo Code (Read only) */}
          {partner?.promo_code && (
            <div>
              <label className="text-xs uppercase tracking-widest font-semibold text-[#FF9A00] mb-1 block">Your Promo Code</label>
              <div className="flex gap-2">
                <Input value={partner.promo_code} readOnly
                  className="bg-black/20 border-white/10 text-[#F5E9CF] cursor-default font-bold tracking-wider" />
                <Button variant="ghost" size="icon" className="shrink-0 text-[#FF9A00] hover:text-[#FFB033]"
                  onClick={() => { navigator.clipboard.writeText(partner.promo_code); toast.success('Promo code copied!'); }}
                  data-testid="copy-promo-settings">
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-[10px] text-[#B2C7C9] mt-1">Set by admin. Share this code with your audience.</p>
            </div>
          )}

          <Button onClick={handleSave} disabled={saving}
            className="w-full rounded-xl font-bold text-[#195259]"
            style={{ background: '#FF9A00' }} data-testid="save-settings-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save Changes'}
          </Button>
        </CardContent>
      </Card>

      {/* Change Password Card */}
      <Card className="border-0" style={{ background: 'rgba(255,255,255,0.05)', backdropFilter: 'blur(16px)', border: '1px solid rgba(255,255,255,0.08)' }}>
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h4 className="text-sm font-bold text-[#F5E9CF]">Change Password</h4>
              <p className="text-xs text-[#B2C7C9]">Update your login password</p>
            </div>
            {!showPwForm && (
              <Button variant="ghost" size="sm" className="text-[#FF9A00] hover:text-[#FFB033] text-xs"
                onClick={() => setShowPwForm(true)} data-testid="toggle-change-password">
                Change
              </Button>
            )}
          </div>
          {showPwForm && (
            <div className="space-y-3">
              <div>
                <label className="text-xs uppercase tracking-widest font-semibold text-[#B2C7C9] mb-1 block">Current Password</label>
                <Input type="password" value={currentPw} onChange={e => setCurrentPw(e.target.value)}
                  className="bg-black/20 border-white/10 text-[#F5E9CF] focus:border-[#FF9A00]"
                  placeholder="Enter current password" data-testid="current-password-input" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest font-semibold text-[#B2C7C9] mb-1 block">New Password</label>
                <Input type="password" value={newPw} onChange={e => setNewPw(e.target.value)}
                  className="bg-black/20 border-white/10 text-[#F5E9CF] focus:border-[#FF9A00]"
                  placeholder="Enter new password (min 6 chars)" data-testid="new-password-input" />
              </div>
              <div className="flex gap-2">
                <Button onClick={handleChangePassword} disabled={changingPw}
                  className="rounded-xl font-bold text-[#195259]"
                  style={{ background: '#FF9A00' }} data-testid="submit-change-password">
                  {changingPw ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Update Password'}
                </Button>
                <Button variant="ghost" onClick={() => { setShowPwForm(false); setCurrentPw(''); setNewPw(''); }}
                  className="text-[#B2C7C9]">Cancel</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ==================== MAIN PARTNERSHIP PAGE ====================
export default function PartnershipPage() {
  const [authed, setAuthed] = useState(false);
  const [token, setToken] = useState('');
  const [partner, setPartner] = useState(null);
  const [activeSection, setActiveSection] = useState('dashboard');
  const [dashData, setDashData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Check saved session
  useEffect(() => {
    const saved = sessionStorage.getItem('partner_token');
    const savedPartner = sessionStorage.getItem('partner_data');
    if (saved && savedPartner) {
      setToken(saved);
      try { setPartner(JSON.parse(savedPartner)); } catch {}
      setAuthed(true);
    }
  }, []);

  const handleAuth = (t, p) => {
    setToken(t); setPartner(p); setAuthed(true);
    sessionStorage.setItem('partner_token', t);
    sessionStorage.setItem('partner_data', JSON.stringify(p));
  };

  const handleLogout = () => {
    setAuthed(false); setToken(''); setPartner(null); setDashData(null);
    sessionStorage.removeItem('partner_token');
    sessionStorage.removeItem('partner_data');
  };

  // Fetch dashboard data
  useEffect(() => {
    if (!authed || !token) return;
    setLoading(true);
    fetch(`${API}/api/partner/dashboard`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { if (r.status === 401) { handleLogout(); return null; } return r.json(); })
      .then(d => { if (d) setDashData(d); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [authed, token]);

  if (!authed) return <AuthGate onAuth={handleAuth} />;

  const renderSection = () => {
    switch (activeSection) {
      case 'dashboard': return loading ? <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#FF9A00]" /></div> : <DashboardSection data={dashData} />;
      case 'users': return <UserOverviewSection token={token} />;
      case 'income': return <IncomeSection token={token} />;
      case 'library': return <CreativeLibrarySection token={token} />;
      case 'achievements': return <AchievementsSection token={token} />;
      case 'settings': return <SettingsSection token={token} partner={partner} onPartnerUpdate={(p) => { setPartner(p); sessionStorage.setItem('partner_data', JSON.stringify(p)); }} />;
      default: return null;
    }
  };

  return (
    <div className="min-h-screen" style={{ background: '#195259', fontFamily: 'Lexend, sans-serif' }}>
      <FloatingElements />
      <Sidebar active={activeSection} onChange={setActiveSection} partner={partner} onLogout={handleLogout}
        mobileOpen={mobileOpen} onMobileClose={() => setMobileOpen(false)} />

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-14 z-40 flex items-center justify-between px-4"
        style={{ background: '#0F3338', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
        <button onClick={() => setMobileOpen(true)} className="text-[#F5E9CF]" data-testid="mobile-menu-toggle">
          <Menu className="h-6 w-6" />
        </button>
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-[#FF9A00]" />
          <span className="text-sm font-bold text-[#F5E9CF]">Partner Hub</span>
        </div>
        <button onClick={handleLogout} className="text-[#B2C7C9] hover:text-red-400" data-testid="mobile-logout">
          <LogOut className="h-5 w-5" />
        </button>
      </div>

      {/* Main Content */}
      <main className="lg:ml-[260px] pt-14 lg:pt-0 min-h-screen relative z-10">
        <div className="p-4 sm:p-6 lg:p-8 max-w-5xl">
          {/* Section Header */}
          <div className="mb-6">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#F5E9CF] font-[Lexend]">
              {NAV_ITEMS.find(n => n.id === activeSection)?.label || 'Dashboard'}
            </h2>
          </div>
          {renderSection()}
        </div>
      </main>
    </div>
  );
}
